import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import { useOutsideClick } from '@genially/design-system';
import { MoveIcon } from '@genially/icons';
import { isTabletOrMobile } from '@genially/ui/build/utils/devices/device';

import { GameControlButton } from '../../../../../../../../../../shared/application/components/GameControlButton/GameControlButton';
import { usePlayerGameContext } from '../../../../PlayerGame.context';
import { HandQueueTooltip } from './components/HandQueueTooltip/HandQueueTooltip';
import {
  AnimatedWrapper,
  CountBadge,
  Wrapper,
} from './HandQueueGameControlButton.styled';

export const HandQueueGameControlButton = observer(() => {
  const { handQueue } = usePlayerGameContext();

  const count = handQueue.length;

  const [showTooltip, setShowTooltip] = useState(false);

  const wrapperRef = useOutsideClick<HTMLDivElement>(() => {
    if (isTabletOrMobile) setShowTooltip(false);
  });

  const prevCountRef = useRef(count);
  const animationKeyRef = useRef(0);

  if (count > prevCountRef.current) {
    animationKeyRef.current += 1;
  }

  prevCountRef.current = count;

  useEffect(() => {
    if (count === 0) {
      setShowTooltip(false);
    }
  }, [count]);

  // Clicks inside iframes don't bubble to the parent document, so useOutsideClick
  // won't fire. window blur catches focus moving into an iframe on mobile.
  useEffect(() => {
    if (!isTabletOrMobile) return;

    const handleWindowBlur = () => setShowTooltip(false);

    window.addEventListener('blur', handleWindowBlur);
    return () => window.removeEventListener('blur', handleWindowBlur);
  }, []);

  const handleHover = () => {
    setShowTooltip(true);
  };

  const handleBlur = () => {
    setShowTooltip(false);
  };

  const handleClick = () => {
    setShowTooltip(prev => !prev);
  };

  return (
    <Wrapper $isVisible={count > 0} ref={wrapperRef}>
      <AnimatedWrapper key={animationKeyRef.current}>
        <GameControlButton
          Icon={MoveIcon}
          expandRight
          onClick={isTabletOrMobile ? handleClick : undefined}
          onHover={isTabletOrMobile ? undefined : handleHover}
          onBlur={isTabletOrMobile ? undefined : handleBlur}
        >
          {count > 0 && <CountBadge>{count}</CountBadge>}
        </GameControlButton>
      </AnimatedWrapper>

      <HandQueueTooltip
        players={handQueue}
        show={showTooltip}
        referenceElement={wrapperRef}
      />
    </Wrapper>
  );
});
